// Typed API client for the dundial backend.
// Contracts are verbatim from the B2 dashboard_reads.go DTOs.
// All fetch calls use apiFetch (credentials:'include', non-2xx → ApiError).

import { apiFetch } from "@avagency/frontend/lib/api";

// ──────────────────────── Dashboard ────────────────────────

export interface DashboardCounters {
  open: number;
  recovered: number;
  exhausted: number;
  cancelled: number;
  free_tier_observed: number;
  recovered_amount_cents: number;
  risk_amount_cents: number;
}

export async function fetchDashboard(): Promise<DashboardCounters> {
  return apiFetch<DashboardCounters>("/api/v1/dashboard");
}

// ──────────────────────── Recoveries ────────────────────────

export interface Recovery {
  id: string;
  integration_id: string;
  failed_invoice_id: string;
  source: string;
  recovered_amount_cents: number;
  currency: string;
  stripe_charge_id: string;
  recovered_at: string; // RFC 3339
}

interface RecoveriesResponse {
  recoveries: Recovery[];
}

export async function fetchRecoveries(limit = 100): Promise<Recovery[]> {
  const body = await apiFetch<RecoveriesResponse>(
    `/api/v1/recoveries?limit=${limit}`
  );
  return body.recoveries;
}

// ──────────────────────── Failed invoices ────────────────────────

export interface FailedInvoice {
  id: string;
  integration_id: string;
  stripe_invoice_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  decline_code: string;
  decline_strategy: string;
  amount_cents: number;
  currency: string;
  first_failed_at: string; // RFC 3339
  next_retry_at: string | null; // RFC 3339, absent when not scheduled
  attempts_made: number;
  status: string; // open | recovered | exhausted | cancelled | free_tier_observed
  on_exhausted: string;
  created_at: string; // RFC 3339
  updated_at: string; // RFC 3339
}

interface FailedInvoicesResponse {
  failed_invoices: FailedInvoice[];
}

export async function fetchFailedInvoices(
  params: { limit?: number; status?: string } = {}
): Promise<FailedInvoice[]> {
  const sp = new URLSearchParams();
  if (params.limit !== undefined) sp.set("limit", String(params.limit));
  if (params.status) sp.set("status", params.status);
  const qs = sp.toString();
  const body = await apiFetch<FailedInvoicesResponse>(
    `/api/v1/failed-invoices${qs ? `?${qs}` : ""}`
  );
  return body.failed_invoices;
}
